import dayjs from 'dayjs';
import { EnrollmentTemplateSettings, TemplateFieldControl } from '../types';

/**
 * Fields the backend mandates for enrollment campaigns. Their Visible/Required
 * toggles are hidden in the config builder and forced on by normalize().
 */
export const REQUIRED_ENROLLMENT_TEMPLATE_FIELDS: (keyof EnrollmentTemplateSettings)[] = [
  'joinStartAt',
  'joinEndAt',
];

const control = <T>(value: T, overrides?: Partial<TemplateFieldControl<T>>): TemplateFieldControl<T> => ({
  visible: true,
  required: false,
  default: value,
  ...overrides,
});

/** Default template-level enrollment settings (empty join window by default). */
export const createDefaultEnrollmentTemplateSettings = (): EnrollmentTemplateSettings => ({
  visible: true,
  joinStartAt: control<string>('', { required: true }),
  joinEndAt: control<string>('', { required: true }),
});

const normalizeControl = <T>(
  raw: Partial<TemplateFieldControl<T>> | undefined,
  fallback: TemplateFieldControl<T>,
  forceOn = false,
): TemplateFieldControl<T> => ({
  visible: forceOn ? true : typeof raw?.visible === 'boolean' ? raw.visible : fallback.visible,
  required: forceOn ? true : typeof raw?.required === 'boolean' ? raw.required : fallback.required,
  default: raw?.default !== undefined ? (raw.default as T) : fallback.default,
  label: raw?.label ?? fallback.label,
});

const isRequiredField = (key: keyof EnrollmentTemplateSettings) =>
  REQUIRED_ENROLLMENT_TEMPLATE_FIELDS.includes(key);

/**
 * Coerce a partial / loaded enrollment settings block into the full shape.
 * Join window fields are forced visible + required (backend-mandated).
 */
export const normalizeEnrollmentTemplateSettings = (
  settings?: Partial<EnrollmentTemplateSettings> | null,
): EnrollmentTemplateSettings => {
  const defaults = createDefaultEnrollmentTemplateSettings();
  return {
    visible: typeof settings?.visible === 'boolean' ? settings.visible : defaults.visible,
    joinStartAt: normalizeControl(settings?.joinStartAt, defaults.joinStartAt, isRequiredField('joinStartAt')),
    joinEndAt: normalizeControl(settings?.joinEndAt, defaults.joinEndAt, isRequiredField('joinEndAt')),
  };
};

/**
 * Validate the join-window default values: end must be after start.
 */
export const validateEnrollmentTemplateSettings = (
  settings?: EnrollmentTemplateSettings | null,
): Record<string, string> => {
  const errors: Record<string, string> = {};
  if (!settings) return errors;

  const start = settings.joinStartAt.default ? dayjs(settings.joinStartAt.default) : null;
  const end = settings.joinEndAt.default ? dayjs(settings.joinEndAt.default) : null;

  if (start?.isValid() && end?.isValid() && !end.isAfter(start)) {
    errors['enrollmentSettings.joinEndAt'] = 'Join window end must be after the start.';
  }

  return errors;
};
